import { z } from 'zod/v4';

/**
 * Sort columns the admin tracing-orders list accepts. Each key maps to a SQL
 * expression in `core/db/tracing-orders/list-union.ts` (`SORT_COLUMNS`) — keep
 * the two in sync when adding/removing columns.
 *
 * `orderType` is intentionally excluded: the legacy schema's
 * `SORT_UNION_COLUMNS_ENUM` declared it, but the legacy sort-column mapping
 * never wired it to a SQL expression, so it silently fell back to the
 * default order — an accepted-but-ignored column, dropped here.
 */
export const AdminTracingOrdersSortColumns = [
  'updatedAt',
  'receiverCompanyName',
  'supplierCompanyName',
  'buyerCompanyName',
  'orderNumber',
  'orderId',
  'docCompletionPercentage',
  'scDocCompletionPercentage',
  'lastProductionProcessId',
  'tags',
] as const;

export type AdminTracingOrdersSortColumn =
  (typeof AdminTracingOrdersSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC)(,[a-zA-Z]+:(ASC|DESC))*$/)
  .optional();

export const AdminTracingOrdersQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  filter: z.string().max(512).optional(),
});

export type AdminTracingOrdersQuery = z.infer<
  typeof AdminTracingOrdersQuerySchema
>;

/**
 * Row shape returned by the admin tracing-orders list. `looseObject` because
 * the SQL returns ~30 columns and strictly typing all of them here duplicates
 * `backend/src/types` without buying validation strength for an admin-only
 * read endpoint — but the fields a consumer actually reads (verified against
 * `core/db/tracing-orders/types.ts`'s `RawUnionTracingOrderRow` and
 * `list-union.ts`'s `transformRow`) are typed strictly below, so frontend
 * code can `z.infer` this instead of hand-rolling a parallel type.
 */
export const AdminTracingOrderRowSchema = z.looseObject({
  id: z.string(),
  orderNumber: z.string().nullable().optional(),
  isPurchaseOrder: z.number(),
  buyerCompanyId: z.string().nullable().optional(),
  buyerCompanyName: z.string().nullable().optional(),
  supplierCompanyId: z.string().nullable().optional(),
  supplierCompanyName: z.string().nullable().optional(),
  receiverCompanyId: z.string().nullable().optional(),
  receiverCompanyName: z.string().nullable().optional(),
  lastProductionProcessName: z.string().nullable().optional(),
  scDocCompletionPercentage: z.number().nullable().optional(),
  docCompletionPercentage: z.number().nullable().optional(),
  aiSummary: z
    .looseObject({
      scAnalysis: z.unknown().optional(),
      docType: z.unknown().optional(),
      docContent: z.unknown().optional(),
    })
    .nullable()
    .optional(),
  /** A genuine `Date` at runtime, confirmed via the live serializer (see the
   *  equivalent note on `AdminCompanyListRowSchema.updatedAt`). */
  updatedAt: z.coerce.date().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
});

export type AdminTracingOrderRow = z.infer<typeof AdminTracingOrderRowSchema>;

// The envelope (metadata.pagination + data) is applied at the route via
// buildResponseSchemas({ paginated: true }) — this is just the row array.
export const AdminTracingOrdersResponseSchema = z.array(
  AdminTracingOrderRowSchema,
);

export type AdminTracingOrdersResponse = z.infer<
  typeof AdminTracingOrdersResponseSchema
>;
